import {
  Badge,
  Card,
  EmptyState,
  PageHeader,
  Row,
  SkeletonList,
  Stack,
  type BadgeTone,
} from '@dental-staffing-platform/ui';
import { adminApi } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';

function statusTone(status: string): BadgeTone {
  if (status === 'healthy' || status === 'connected') return 'success';
  if (status === 'degraded') return 'warning';
  return 'danger';
}

export function SystemHealth() {
  const state = useAsync(() =>
    Promise.all([adminApi.system.health(), adminApi.system.integrations()])
  );

  return (
    <div>
      <PageHeader
        title="System health"
        subtitle="Runtime dependencies and launch integration readiness"
      />
      {state.loading ? (
        <SkeletonList count={4} />
      ) : state.error || !state.data ? (
        <EmptyState
          title="Health check unavailable"
          description={state.error ?? 'The health request did not complete.'}
        />
      ) : (
        <Stack gap={16}>
          <Card>
            <h2 style={{ fontSize: '1rem', marginBottom: 12 }}>Services</h2>
            <Stack gap={0}>
              {state.data[0].map((service, index) => (
                <Row
                  key={service.service}
                  style={{
                    justifyContent: 'space-between',
                    padding: '12px 0',
                    borderTop: index === 0 ? 'none' : '1px solid var(--dsp-border)',
                  }}
                >
                  <div>
                    <strong>{service.service}</strong>
                    <div className="dsp-muted" style={{ fontSize: '0.82rem' }}>
                      {service.environment} · checked {new Date(service.checkedAt).toLocaleString()}
                    </div>
                  </div>
                  <Badge tone={statusTone(service.status)}>{service.status}</Badge>
                </Row>
              ))}
            </Stack>
          </Card>

          <Card>
            <h2 style={{ fontSize: '1rem', marginBottom: 4 }}>External delivery</h2>
            <p className="dsp-muted" style={{ fontSize: '0.86rem', margin: '0 0 12px' }}>
              Disconnected providers are launch integrations; in-app notifications remain available.
            </p>
            <Stack gap={0}>
              {state.data[1].map((integration, index) => (
                <Row
                  key={integration.id}
                  style={{
                    justifyContent: 'space-between',
                    padding: '12px 0',
                    borderTop: index === 0 ? 'none' : '1px solid var(--dsp-border)',
                  }}
                >
                  <strong>{integration.provider}</strong>
                  <Badge tone={statusTone(integration.status)}>{integration.status}</Badge>
                </Row>
              ))}
            </Stack>
          </Card>
        </Stack>
      )}
    </div>
  );
}
